import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { message } from 'antd';
import { useMemo } from 'react';
import { useSelector } from 'react-redux';

import { trackButtonClick } from '../../analytics/mixpanel';
import { handleAxiosError } from '../../CommonFuctions/handleAxiosError';
import { axiosDocChatInstance } from '../../lib/axiosDocChatInstance';
import { useTakeTour } from '../TakeTour/hooks/useTakeTour';
import { useSelectedUploadData } from './useSelectedUploadData';

const fetchPresets = async () => {
  const { data } = await axiosDocChatInstance.get(`/api/presets/`);
  return data;
};

const fetchPresetResponse = async ({ user_id: userId, recordType, uuid, signal }) => {
  const { data } = await axiosDocChatInstance.get(
    `/api/presets/responses/${recordType.toLocaleLowerCase()}/${userId}/${uuid}/`,
    { signal }
  );
  return data;
};

const generatePresetMutationFn = async (payload) => {
  const res = await axiosDocChatInstance.post('/api/presets/generate/', payload);
  return res;
};

const presetClearChatMutationFn = async ({ type, id, payload }) => {
  const res = await axiosDocChatInstance.delete(`/api/presets/clear/${type}/${id}/ `, payload);
  return res;
};

const createPresetMutationFn = async (payload) => {
  const res = await axiosDocChatInstance.post('/api/presets/', { ...payload });
  return res;
};

const updatePresetMutationFn = async ({ preset_id, payload }) => {
  const res = await axiosDocChatInstance.put(`/api/presets/${preset_id}/`, payload);
  return res;
};

const deletePresetMutationFn = async (preset_id) => {
  const res = await axiosDocChatInstance.delete(`/api/presets/${preset_id}/`);
  return res;
};

const presetFeedbackMutationFn = async (payload) => {
  const res = await axiosDocChatInstance.post('/api/presets/feedback/', payload);
  return res;
};

export const usePresets = () => {
  const { user_id } = useSelector((state) => state.loginState);

  const { recordType, record, uuid } = useSelectedUploadData();
  const { isOpen } = useTakeTour();
  const presetsQueryKey = ['presets'];
  const {
    isPending: presetsLoading,
    error: presetsError,
    data: presetsData,
  } = useQuery({
    queryKey: presetsQueryKey,
    queryFn: () => fetchPresets(),
    staleTime: 5 * 60 * 1000,
  });

  const presetResponseQueryKey = ['presetsResponse', recordType, user_id, uuid];

  const queryClient = useQueryClient();

  const {
    isPending: presetResponsesLoading,
    error: presetResponsesError,
    data: presetResponsesData,
  } = useQuery({
    queryKey: presetResponseQueryKey,
    queryFn: ({ signal }) => fetchPresetResponse({ user_id, recordType, uuid, signal }),
    enabled: !!(recordType && user_id && uuid) && !isOpen,
    staleTime: 2 * 60 * 60 * 1000,
    gcTime: 2 * 60 * 60 * 1000,
  });

  const allPresets = useMemo(() => presetsData?.presets || [], [presetsData]);
  const topUsedPresets = useMemo(() => presetsData?.top_used_presets || [], [presetsData]);

  const presetResponses = useMemo(() => {
    return [...(presetResponsesData?.preset_runs || [])]?.reverse() || [];
  }, [presetResponsesData]);

  const presets = useMemo(() => {
    return allPresets.filter((preset) => preset.preset_type === 'default');
  }, [allPresets]);

  const workflows = useMemo(() => {
    return allPresets.filter((preset) => preset.preset_type === 'user');
  }, [allPresets]);

  const {
    variables: generatePresetVariables,
    mutateAsync: generatePresetPost,
    data: generatePresetData,
    isPending: generatePresetLoading,
    error: generatePresetError,
  } = useMutation({
    mutationFn: generatePresetMutationFn,
    onMutate: async (variables) => {
      const preset = allPresets.find((p) => p.preset_id === variables.preset_id);

      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: presetResponseQueryKey });

      // Snapshot current cache
      const previousData = queryClient.getQueryData(presetResponseQueryKey);

      const tempResponseId = `temp-${Date.now()}`;

      const presetResponseObj = {
        created_at: new Date(),
        feedback: null,
        preset_description: preset.preset_description,
        preset_id: preset.preset_id,
        preset_name: preset.preset_name,
        run_id: tempResponseId,
      };

      // Optimistically update cache
      queryClient.setQueryData(presetResponseQueryKey, (old) => {
        if (!old) return old;

        return {
          ...old,
          preset_runs: [presetResponseObj, ...(old.preset_runs ?? [])],
        };
      });

      // Return context for rollback
      return {
        previousData,
        queryKey: [...presetResponseQueryKey],
        run_id: tempResponseId,
      };
    },

    onSuccess: ({ data }, _vars, context) => {
      queryClient.setQueryData(context.queryKey, (old) => {
        if (!old) return old;

        return {
          ...old,
          preset_runs: old.preset_runs?.map((r) => {
            if (r.run_id === context.run_id)
              return {
                ...r,
                failed_documents: data.failed_documents,
                successful_documents: data.successful_documents,
                run_id: data.run_id,
              };
            return r;
          }),
        };
      });
    },

    onError: (error, _vars, context) => {
      handleAxiosError(error);
      console.error('Generate preset failed', error);
      queryClient.setQueryData(context.queryKey, (old) => {
        if (!old) return old;

        return {
          ...old,
          preset_runs: old.preset_runs?.map((r) => {
            if (r.run_id === context.run_id)
              return {
                ...r,
                run_id: `failed-${Date.now()}`,
              };
            return r;
          }),
        };
      });
    },
  });

  const { mutateAsync: presetClearChatDelete } = useMutation({
    mutationFn: presetClearChatMutationFn,
    onMutate: async () => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: presetResponseQueryKey });

      // Snapshot current cache
      const previousData = queryClient.getQueryData(presetResponseQueryKey);

      // Optimistically update cache
      queryClient.setQueryData(presetResponseQueryKey, (old) => {
        if (!old) return old;

        return {
          ...old,
          preset_runs: [],
        };
      });

      // Return context for rollback
      return { previousData, queryKey: [...presetResponseQueryKey] };
    },
    onError: (error, _vars, context) => {
      handleAxiosError(error);
      console.error('Preset chat clear failed', error);
      queryClient.setQueryData(context.queryKey, (old) => {
        if (!old) return old;

        return {
          ...old,
          preset_runs: [...old.preset_runs, ...context.previousData.preset_runs],
        };
      });
    },
  });

  const {
    mutateAsync: createPreset,
    isPending: createPresetLoading,
    error: createPresetError,
  } = useMutation({
    mutationFn: createPresetMutationFn,
    onSuccess: () => {
      message.success('Workflow created successfully');
      queryClient.invalidateQueries({ queryKey: presetsQueryKey });
    },
    onError: (error) => {
      handleAxiosError(error);
      console.error('Create preset failed', error);
    },
  });

  const {
    mutateAsync: updatePreset,
    isPending: updatePresetLoading,
    error: updatePresetError,
  } = useMutation({
    mutationFn: updatePresetMutationFn,
    onSuccess: () => {
      message.success('Workflow updated successfully');
      queryClient.invalidateQueries({ queryKey: presetsQueryKey });
      queryClient.invalidateQueries({ queryKey: [presetResponseQueryKey[0]] });
    },
    onError: (error) => {
      handleAxiosError(error);
      console.error('Update preset failed', error);
    },
  });

  const {
    mutateAsync: deletePreset,
    isPending: deletePresetLoading,
    error: deletePresetError,
  } = useMutation({
    mutationFn: deletePresetMutationFn,
    onSuccess: () => {
      message.success('Workflow deleted successfully');
      queryClient.invalidateQueries({ queryKey: presetsQueryKey });
      queryClient.invalidateQueries({ queryKey: [presetResponseQueryKey[0]] });
    },
    onError: (error) => {
      handleAxiosError(error);
      console.error('Delete preset failed', error);
    },
  });

  const { mutateAsync: presetFeedbackPost } = useMutation({
    mutationFn: presetFeedbackMutationFn,
    onMutate: (variables) => {
      // Optimistically update cache
      queryClient.setQueryData(presetResponseQueryKey, (old) => {
        if (!old) return old;

        return {
          ...old,
          preset_runs: old.preset_runs.map((r) => {
            if (r.run_id === variables.run_id) return { ...r, ...variables };
            return r;
          }),
        };
      });

      // Return context for rollback
      return { queryKey: [...presetResponseQueryKey] };
    },
    onSuccess: () => message.success('Thank you for your feedback'),
    onError: (error, variables, context) => {
      handleAxiosError(error);
      console.error('Preset chat feedback submit failed', error);
      queryClient.setQueryData(context.queryKey, (old) => {
        if (!old) return old;

        return {
          ...old,
          preset_runs: old.preset_runs.map((r) => {
            if (r.run_id === variables.run_id) return { ...r, feedback: null };
            return r;
          }),
        };
      });
    },
  });

  const presetChat = useMemo(() => {
    const tempPresetChat = presetResponses
      .map((v) => {
        return [
          {
            role: 'user',
            content: `**${v.preset_name}**  \n${v.preset_description || 'No description available'}`,
            timestamp: v.created_at,
          },
          {
            run_id: v.run_id,
            role: 'assistant',
            feedback: v.feedback,
            preset_id: v.preset_id,
            failedDocuments: v.failed_documents || [],
            successfulDocuments: v.successful_documents || [],
          },
        ];
      })
      .flat();

    if (presetResponses[presetResponses.length - 1]?.run_id?.startsWith('temp-'))
      tempPresetChat.pop();

    return tempPresetChat || [];
  }, [presetResponses]);

  const generatePreset = async (preset_id) => {
    trackButtonClick({ type: `Generate Preset - ${preset_id}` });
    try {
      const res = await generatePresetPost({
        userID: user_id,
        type: recordType?.toLowerCase?.(),
        ID: recordType === 'COLLECTION' ? record.collection_id : record.document_id,
        preset_id,
      });
      return res;
    } catch (err) {
      console.error(err);
    }
  };

  const handlePresetClearChat = () => {
    trackButtonClick({ type: 'Preset Clear Chat' });
    if (generatePresetLoading && generatePresetVariables.ID === uuid)
      return message.info('Please wait for the workflow to finish generating');

    presetClearChatDelete({
      type: recordType?.toLowerCase?.(),
      id: recordType === 'COLLECTION' ? record.collection_id : record.document_id,
      payload: { userID: user_id },
    });
  };

  return {
    allPresets,
    presets,
    workflows,
    defaultPresets: presets,
    presetsLoading,
    presetsError,
    presetChat,
    presetResponsesLoading,
    presetResponsesError,
    generatePresetVariables,
    generatePreset,
    generatePresetData,
    generatePresetLoading,
    generatePresetError,
    handlePresetClearChat,
    presetFeedbackPost,
    createPreset,
    createPresetLoading,
    createPresetError,
    updatePreset,
    updatePresetLoading,
    updatePresetError,
    deletePreset,
    deletePresetLoading,
    deletePresetError,
    topUsedPresets,
  };
};
